// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {get, omit, set} from 'lodash';

// Core Libs
import {parseJson} from '../../lib/core/object';

// Components
import DataViewer from '../core/data-viewer';
import Input from '../core/input';
import Loading from '../core/loading';
import RecordDetails from '../core/record-details';
import ResetButton from '../core/reset-button';
import ServicesSearchForm from '../app/services-search-form';

// Containers
import withSearch from '../../containers/with-search';

// Actions
import {setFieldValue} from '../../actions/form-data-actions';

// Styles
import './mediaitem-json.scss';

const servicesDataKey = 'mediaItemJsonCatalog';

class CatalogDetail extends Component {
    state = {
        echostarId: null,
        externalGuid: null,
        resourceId: null,
    };

    componentWillMount() {
        if (get(this.getMediaItem(), 'guid')) {
            this.setFormValues();
            this.setState({
                echostarId: this.getMediaItemEchostarId(),
                externalGuid: this.getMediaItemExternalGuid(),
                resourceId: this.getMediaItemResourceId(),
            });
        }
    }

    componentWillUpdate(nextProps) {
        const {fetching, formValues} = this.props;

        if (this.getMediaItemGuid() !== this.getMediaItemGuid(nextProps)) {
            this.setFormValues(nextProps);
        } else if (!fetching && nextProps.fetching) {
            this.setState({
                echostarId: get(nextProps.formValues, 'echostarId'),
                externalGuid: get(nextProps.formValues, 'externalGuid'),
                resourceId: get(nextProps.formValues, 'resourceId'),
            });

            if (!get(formValues, 'echostarId') && !get(formValues, 'resourceId') && !get(formValues, 'externalGuid')) {
                this.setFormValues(nextProps);
            }
        }
    }

    getFormProps() {
        return {
            className: 'mediaitem-json--form',
            noFocus: true,
            noRefreshOnReset: true,
            noScroll: true,
            onSubmit: this.handleFormSubmit,
            servicesDataKey,
        };
    }

    getMediaItem(props = this.props) {
        return get(props.dependentSearchValues, 'content.0');
    }

    getMediaItemEchostarId(props = this.props) {
        const mediaItem = this.getMediaItem(props);

        return get(mediaItem, 'amsEpisode.echostarId', get(mediaItem, 'amsFranchise.echostarId'));
    }

    getMediaItemExternalGuid(props = this.props) {
        const mediaItem = this.getMediaItem(props);

        return get(mediaItem, 'externalGuid');
    }

    getMediaItemGuid(props = this.props) {
        const mediaItem = this.getMediaItem(props);

        return get(mediaItem, 'guid');
    }

    getMediaItemResourceId(props = this.props) {
        const mediaItem = this.getMediaItem(props);

        return get(mediaItem, 'offerings.0.resourceId');
    }

    getRecordData() {
        const searchData = this.getSearchData();
        const formattedJson = parseJson(searchData.json, {});

        return formattedJson;
    }

    getRecordDetailsProps() {
        const searchData = this.getSearchData();
        const {echostarId, externalGuid, resourceId} = searchData;
        const {echostarId: echostarIdState, externalGuid: externalGuidState, resourceId: resourceIdState} = this.state;

        if (!echostarId) {
            set(searchData, 'echostarId', echostarIdState);
        }

        if (!externalGuid) {
            set(searchData, 'externalGuid', externalGuidState);
        }

        if (!resourceId) {
            set(searchData, 'resourceId', resourceIdState);
        }

        return {
            blacklistKeys: ['json'],
            className: 'mediaitem-json--record-details',
            noDetailExpand: true,
            searchData: [
                {
                    ...searchData,
                },
            ],
            whitelistKeys: ['echostarId', 'externalGuid', 'resourceId'],
        };
    }

    getSearchData() {
        const {searchData} = this.props;

        return {...get(searchData, '0', {})};
    }

    setFormValues(props = this.props) {
        const {echostarId: echostarIdState, externalGuid: externalGuidState, resourceId: resourceIdState} = this.state;
        const {formValues} = this.props;
        const echostarId = this.getMediaItemEchostarId(props);

        if (echostarIdState !== echostarId || !get(formValues, 'echostarId')) {
            props.setFieldValue(servicesDataKey, 'echostarId', echostarId);
        }

        const externalGuid = this.getMediaItemExternalGuid(props);

        if (externalGuidState !== externalGuid || !get(formValues, 'externalGuid')) {
            props.setFieldValue(servicesDataKey, 'externalGuid', externalGuid);
        }

        const resourceId = this.getMediaItemResourceId(props);

        if (resourceIdState !== resourceId || !get(formValues, 'resourceId')) {
            props.setFieldValue(servicesDataKey, 'resourceId', resourceId);
        }
    }

    handleChange = (event) => {
        const {setFieldValue} = this.props;

        Object.keys(omit(this.state, event.target.name)).map((key) => {
            setFieldValue(servicesDataKey, key, '');
        });
    };

    handleFormSubmit = () => {
        const {formValues, refreshServicesData, setFieldValue} = this.props;

        Promise.all([
            setFieldValue(
                'mediaItemsEnhanced',
                'amsFranchise.echostarId||amsEpisode.echostarId',
                get(formValues, 'echostarId')
            ),
            setFieldValue('mediaItemsEnhanced', 'externalGuid', get(formValues, 'externalGuid')),
            setFieldValue('mediaItemsEnhanced', 'offerings.resourceId', get(formValues, 'resourceId')),
        ]).then(() => {
            refreshServicesData('mediaItemsEnhanced');
        });
    };

    renderDataViewer() {
        const {searchData} = this.props;

        return searchData.length ? (
            <DataViewer
                className="mediaitem-json--data-viewer"
                defaultExpandKeys={['onlinenow', 'content']}
                searchData={[this.getRecordData()]}
            />
        ) : (
            <div className="mediaitem-json--no-items">Catalog data not available for this Media Item.</div>
        );
    }

    renderForm() {
        return (
            <ServicesSearchForm {...this.getFormProps()}>
                <Input
                    label="Echostar ID"
                    name="echostarId"
                    submitOnBlur={true}
                    validate={['number']}
                    onChange={this.handleChange}
                />
                <Input label="RUID" name="resourceId" submitOnBlur={true} onChange={this.handleChange} />
                <Input label="External GUID" name="externalGuid" submitOnBlur={true} onChange={this.handleChange} />
                <ResetButton className="mediaitem-json--reset-button" />
            </ServicesSearchForm>
        );
    }

    renderRecordDetails() {
        return <RecordDetails {...this.getRecordDetailsProps()} />;
    }

    render() {
        const {fetching} = this.props;

        return (
            <div className="mediaitem-json">
                {this.renderForm()}
                <div className="mediaitem-json--title">Search Information</div>
                {this.renderRecordDetails()}
                <div className="mediaitem-json--title">Catalog Data</div>
                {this.renderDataViewer()}
                <Loading loading={fetching} />
            </div>
        );
    }
}

CatalogDetail.propTypes = {
    dashboardProps: PropTypes.object,
    dependentSearchValues: PropTypes.object,
    error: PropTypes.string,
    fetching: PropTypes.bool,
    formValues: PropTypes.object.isRequired,
    refreshServicesData: PropTypes.func.isRequired,
    routerParams: PropTypes.object.isRequired,
    searchData: PropTypes.array.isRequired,
    setFieldValue: PropTypes.func.isRequired,
};

CatalogDetail.defaultProps = {
    dashboardProps: {},
};

const mapDispatchToProps = {setFieldValue};

export default connect(null, mapDispatchToProps)(withSearch(CatalogDetail, servicesDataKey));
